import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Flame, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ variant }) {
  const [state, setState] = useState("confirming");
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (variant !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          localStorage.removeItem("avanti_cart");
          setState("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [variant, sessionId]);

  const content =
    variant === "cancel"
      ? { icon: XCircle, title: "Payment cancelled", text: "No worries — your order wasn't placed. Head back and try again whenever you're ready.", tone: "text-ember" }
      : state === "paid"
      ? { icon: CheckCircle2, title: "Order confirmed & paid", text: "Thanks! We're firing it up now. Come in and pick it up — just give your name at the counter.", tone: "text-ember" }
      : state === "timeout"
      ? { icon: Loader2, title: "Checking your payment…", text: "It's taking a little longer than usual. If you were charged, your order is with us — call (03) 5024 5570 to confirm.", tone: "text-ember" }
      : { icon: Loader2, title: "Confirming your payment…", text: "Hold tight, we're checking with the bank.", tone: "text-ember", spin: true };

  const Icon = content.icon;

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid={`payment-${variant}-page`}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "circOut" }}
        className="w-full max-w-md text-center border border-zinc-800 bg-white/60 backdrop-blur-xl p-10"
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <Flame className="w-5 h-5 text-ember" />
          <span className="font-display text-2xl tracking-wide">AVANTI</span>
        </div>
        <Icon className={`w-12 h-12 mx-auto mb-6 ${content.tone} ${content.spin ? "animate-spin" : ""}`} />
        <h1 className="font-display text-4xl uppercase tracking-wide mb-4" data-testid="payment-result-title">{content.title}</h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8" data-testid="payment-result-text">{content.text}</p>
        <a
          href="/"
          data-testid="payment-back-home"
          className="inline-block bg-ember hover:bg-ember-hover text-white font-semibold px-8 py-3 text-sm tracking-wide uppercase transition-colors duration-300"
        >
          Back to Avanti
        </a>
      </motion.div>
    </div>
  );
}
